import { Suspense, useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import CometChatUnreadSync from './components/CometChatUnreadSync';
import './App.css';

import { CometChatLocalize, CometChatUIKit } from '@cometchat/chat-uikit-react';
import { UIKitSettingsBuilder } from '@cometchat/uikit-shared';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { createContext } from 'react';
import { useSelector } from 'react-redux';
import { Toaster } from 'sonner';
import { TaxSettingsNoticeHost } from './common/TaxSettingsNotice';
import PrivateRoutes from './routes/PrivateRoutes';
import PublicRoutes from './routes/PublicRoutes';

// TanStack Query Provider
import QueryProvider from './providers/QueryProvider';

import { useDispatch } from 'react-redux';
import { COMET_CHAT_APP_ID, COMET_CHAT_AUTH_KEY, COMET_CHAT_REGION } from './config/dataService/dataService';
import AutoUpdateChecker from './components/AutoUpdateChecker';
import { createUser } from './redux/CometChat/action';
import useGlobalStore from './store/useGlobalStore';
import { SocketProvider } from './socket';
import PostHogPageTracker from './components/Analytics/PostHogPageTracker';
import posthog, { getStoredPostHogShopDetails, setupPostHogIdentity } from './config/PostHog/posthog';
import ExportReportModel from './container/Invoice/ExportReportModel';
export const Context = createContext('Default Value');

/* Constants */
export const ACTION_BLUE = '#006CDD';
export const REQUIRED_RED = '#D32F2F';
export const ESTIMATE_REQUEST_COLOR = '#FEBBC3';
export const ESTIMATE_INVOICE_SENT = '#B0BDFC';
export const ESTIMATE_INVOICE_DRAFT = '#FDD79C';
export const ESTIMATE_INVOICE_TABLE_ROW = 'c5d0da';

export const SUPPLIER_INVOICE_STATUS_COLOR = {
    open: '#FCB0DE',
    partial_payment: '#F2FBAD',
    paid_in_full: '#C8F5D1',
    draft: '#FDD79C',
    finalized: '#02E473',
};
export const CUSTOOMER_INVOICE_STATUS_COLOR = {
    FINALIZED: '#C8F5D1',
    DRAFT: '#FDD79C',
    SENT: '#B0E8FC',
};

export const WAITING_FOR_DROP_OFF = '#75DBFF';
export const VEHICLES_DROPPED_OFF = '#FFC30F';
export const IN_PROGRESS = '#FF9E31';
export const READY_FOR_PICK_UP = '#5CE469';

export const APPROVALS_WAITING = '#FFD60A';
export const APPROVALS_ACCEPTED = '#30D158';
export const APPROVALS_DECLINED = '#FF2727';
// const stripePromise = loadStripe(
//   "pk_test_51Nhd0yJxzO4fXIFTmBd94ZK6Z7nYJ41yucmXrULGoURy4SJsphOPa53r83RUIS8hvi8M4RxSfukCZNtjsRC4BOyx00NSroIpzx"
// );
const stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_KEY
    // "pk_test_51Nhd0yJxzO4fXIFTmBd94ZK6Z7nYJ41yucmXrULGoURy4SJsphOPa53r83RUIS8hvi8M4RxSfukCZNtjsRC4BOyx00NSroIpzx"
); /* Theme */
const theme = createTheme({
    palette: {
        /* Call to Action blue */
        primary: {
            main: 'rgba(0, 108, 221, 1)',
            light: 'rgba(0, 108, 221, 0.6)',
        },
        black: {
            main: '#212121',
            light: '#212121',
        },
        /* Apex dark blue */
        secondary: { main: 'rgba(0, 35, 148, 1)' },
        greyColor: { main: 'rgba(0, 0, 0, 0.10)' },
        tabColor: { main: '#2196f3' },

        background: {
            main: 'rgba(250, 251, 255, 1)' /* almost white rgba(250, 251, 255, 1) */,
            dark_grey: 'rgba(241, 241, 241, 1)' /* dark grey */,
            disabled_fields: 'rgba(160, 170, 180, 0.1)' /* light grey */,
        },
        accent: {
            main: '#75DBFF' /* sky blue - used for borders */,
            red: '#D32F2F' /* red - used for required fields */,
            yellow: '#FFD60A' /* red - used for approvals waiting fields */,
        },
        // error: "red",
    },
    /* Custom Theme for MUI Chip used for Estimates, Invoices, Approvals and Customer Profile */
    components: {
        MuiChip: {
            styleOverrides: {
                colorDefault: {
                    backgroundColor: 'rgba(0, 0, 0, 1)' /* black - used for deferred status in approvals */,
                    color: 'rgba(255, 255, 255, 1)',
                    fontWeight: '500',
                },
                colorPrimary: {
                    backgroundColor: 'rgba(176, 189, 252, 1)' /* purple - used in sent status in estimates and invoices */,
                    color: 'rgba(0, 0, 0, 1)',
                    fontWeight: '500',
                },
                colorSecondary: {
                    backgroundColor: 'rgba(253, 215, 156, 1)' /*  mustard yellow - used for draft status in estimates and invoices */,
                    color: 'rgba(0, 0, 0, 1)',
                    fontWeight: '500',
                },
                colorSuccess: {
                    backgroundColor: 'rgba(48, 209, 88, 1)' /*  green - used for approvals approved status */,
                    color: 'rgba(255, 255, 255, 1)',
                    fontWeight: '500',
                },
                colorWarning: {
                    backgroundColor: 'rgba(255, 214, 10, 1)' /* yellow - used for approvals waiting status */,
                    color: 'rgba(0, 0, 0, 1)',
                    fontWeight: '500',
                },
                colorInfo: {
                    backgroundColor: 'rgba(117, 219, 255, 1)' /* sky blue used to identify if customer is shop created or apex auto  */,
                    color: 'rgba(0, 35, 148, 1)' /* apex dark blue */,
                    fontWeight: '500',
                },
                colorError: {
                    backgroundColor: 'rgba(255, 39, 39, 1)' /* red  - used for approvals declined status */,
                    color: 'rgba(255, 255, 255, 1)',
                    fontWeight: '500',
                },
                shopCreatedChip: {
                    backgroundColor: ' rgba(117, 219, 255, 0.5)',
                    color: 'rgba(0, 0, 0, 1)',
                },
                shopImportedChip: {
                    backgroundColor: 'rgba(225, 208, 253, 1)',
                    color: 'rgba(62, 28, 118, 1)',
                },
                apexAutoChip: {
                    backgroundColor: 'rgba(239, 239, 239, 1)',
                    color: 'rgba(0, 35, 148, 1)',
                },
            },
        },
    },
});

// CometChatUIKit.init re-runs its post-login setup (extensions, SDK listeners, calling)
// on every call, so it must only run once per page load — not on every App render.
let cometChatInitPromise = null;
const initCometChat = () => {
    if (!cometChatInitPromise) {
        const UIKitSettings = new UIKitSettingsBuilder()
            .setAppId(COMET_CHAT_APP_ID)
            .setRegion(COMET_CHAT_REGION)
            .setAuthKey(COMET_CHAT_AUTH_KEY)
            .subscribePresenceForFriends()
            .build();

        cometChatInitPromise = CometChatUIKit.init(UIKitSettings);
        cometChatInitPromise.then(
            () => {
                console.log('CometChat Initialized Successfully');
            },
            (error) => {
                console.log('CometChat Initialization Failed with error:', error);
                // Allow the next render to retry
                cometChatInitPromise = null;
            }
        );
    }
    return cometChatInitPromise;
};

function App() {
    const dispatch = useDispatch();
    const [user, setUser] = useState(null);
    console.log('🚀 ~ App ~ user:', user);
    const loggedin = useSelector((state) => state.auth.login);
    const errorMessage = useSelector((state) => state.auth.errorMessage);
    const error = useSelector((state) => state.auth.error);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [isOpenSideBar, setIsOpenSidebar] = useState(true);
    initCometChat();

    const options = {
        // passing the client secret obtained from the server
        clientSecret: '{{CLIENT_SECRET}}',
    };

    // useEffect(()=>{
    //   console.log("caleed");
    //   if(JSON.parse(localStorage.getItem("error"))?.open){
    //     setOpenSnackbar(true)
    //   }
    //   setTimeout(()=>{
    // localStorage.removeItem("error")
    //   },2000)
    // },[JSON.parse(localStorage.getItem("error"))])
    const loginUser = async (id) => {
        // CometChatUIKit.logout();
        // CometChatUIKit.getLoggedinUser().then((user) => {
        //   console.log("useruser",user);
        //   if (user) {
        //     setUser(user);
        //   }
        //   if (!user) {
        //Login user
        CometChatUIKit.login(id, COMET_CHAT_AUTH_KEY)
            .then((user1) => {
                // console.log("Login Successful:", { user1 });
                setUser(user1);
                //mount your app
            })
            .catch(console.log);
        //   } else {
        //   }
        // });
    };

    useEffect(() => {
        const shopDetails = getStoredPostHogShopDetails();

        if (loggedin && shopDetails) {
            setupPostHogIdentity(shopDetails);
        }
    }, [loggedin]);

    useEffect(() => {
        async function initialize() {
            await initCometChat();
            CometChatLocalize.init('en', {
                en: {
                    CHATS: 'Messenger',
                },
            });
        }
        initialize();
    }, []);
    const createLoginUser = async () => {
        if (JSON.parse(localStorage.getItem('apex-saas_details'))) {
            const resp = await dispatch(
                createUser({
                    shopId: JSON.parse(localStorage.getItem('apex-saas_details'))?._id,
                })
            );

            // console.log(
            //   "resp?.data?.data?.chatUserId",
            //   resp?.data?.data?.shopOwnerChatUser?.chatUserId
            // );
            // console.log("resp", resp);

            // console.log("resp COMET_CHAT_AUTH_KEY", COMET_CHAT_AUTH_KEY);
            await loginUser(resp?.data?.data?.shopOwnerChatUser?.chatUserId);
        } else {
            setTimeout(() => {
                createLoginUser();
            }, 1500);
        }
        // setUser(resp?.data);
    };

    useEffect(() => {
        if (!user) {
            createLoginUser();
        }
    }, [user]);

    const { isDirty, resetGlobalState } = useGlobalStore();

    // useEffect(() => {
    //     const handleBeforeUnload = (e) => {
    //         if (isDirty) {
    //             e.preventDefault();
    //             e.returnValue = ''; // Required for Chrome/Edge/Firefox to show the prompt
    //         }
    //     };

    //     window.addEventListener('beforeunload', handleBeforeUnload);
    //     return () => {
    //         window.removeEventListener('beforeunload', handleBeforeUnload);
    //     };
    // }, [isDirty]);

    useEffect(() => {
        // Handle page refresh and tab close
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = ''; // Required for Chrome/Edge/Firefox to show the prompt
            }
        };

        // Handle browser back/forward navigation
        const handlePopState = (e) => {
            if (isDirty) {
                // Name the document being edited, the same way the sidebar's discard dialog does.
                const segments = window.location.pathname.split('/');
                const documentName = segments.includes('work-orders') ? 'work order' : segments.includes('estimates') ? 'estimate' : 'invoice';
                const userConfirmed = window.confirm(`You have unsaved changes. Do you want to discard this ${documentName} or continue editing?`);

                if (!userConfirmed) {
                    // Prevent navigation by pushing the current location back
                    window.history.pushState(null, '', window.location.pathname + window.location.search);
                    return;
                } else {
                    // User confirmed, reset the form state if needed
                    resetGlobalState();
                }
            }
        };

        // Add event listeners
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('popstate', handlePopState);

        // Push current state to history stack when component mounts
        // This helps us detect back button navigation
        window.history.pushState(null, '', window.location.pathname + window.location.search);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isDirty, resetGlobalState]);

    return (
        <>
            {/* TanStack Query Provider - Wraps the entire app for query management */}
            <QueryProvider>
                <SocketProvider>
                    <ThemeProvider theme={theme}>
                        {/* <ErrorSnackbar open={errorMessage !== ""} message={errorMessage} severity={error} /> */}
                        <Context.Provider
                            value={{
                                GOOGLE_MAPS_API_KEY: process.env.REACT_APP_GOOGLE_MAP_KEY,
                                cometChatUser: user,
                                isOpenSideBar,
                                setIsOpenSidebar,
                            }}
                            user={user}
                        >
                            {/* Base path comes from the build: PUBLIC_URL=/apex-saas on development, unset ("/") on production */}
                            <BrowserRouter basename={process.env.PUBLIC_URL || '/'}>
                                <PostHogPageTracker />
                                <AutoUpdateChecker />
                                <ScrollToTop />
                                <Elements
                                    stripe={stripePromise}
                                    //   options={options}
                                >
                                    <Toaster
                                        position="top-center"
                                        richColors
                                        theme="light"
                                        toastOptions={{
                                            style: { borderRadius: '2px' },
                                            className: 'my-toast',
                                        }}
                                    />
                                    <TaxSettingsNoticeHost />
                                    <Suspense>
                                        {loggedin ? (
                                            <>
                                                <CometChatUnreadSync />
                                                <PrivateRoutes />
                                            </>
                                        ) : (
                                            <PublicRoutes />
                                        )}
                                    </Suspense>
                                </Elements>
                            </BrowserRouter>
                        </Context.Provider>

                        {/* <Snackbar
              open={openSnackbar}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              autoHideDuration={2000}
              onClose={()=>setOpenSnackbar(false)}
              key={"top center"}
            >
              <Alert severity="error" sx={{ width: "100%" }}>
              {JSON.parse(localStorage.getItem("error"))?.message}
              </Alert>
            </Snackbar>; */}
                    </ThemeProvider>
                </SocketProvider>
            </QueryProvider>
        </>
    );
}

export default App;
